import * as React from "react";
import {WithApi, WithApiProperties} from "../../common/util/WithApi";
import {defineMessages, InjectedIntlProps, injectIntl} from "react-intl";
import {User} from "../../user/model";
import {Logger} from "../../common/util/Logger";
import {ControlRoomTable, ControlRoomTableProperties} from "../../common/ui/table/ControlRoomTable";
import {UserContentColumnFactory} from "./UsersContentColumnFactory";
import {NewUserButton} from "./NewUserButton";
import {ModalMessageDialog, ModalMessageDialogMode} from "../commons/ModalMessageDialog";
import {ConfirmationDialog} from "../../common/ui/ConfirmationDialog";

const USERS_DATA_LIST_ROW_HEIGHT = 50;
const MESSAGES = defineMessages({
  deleteUserSuccess: {
    id: "studio.products.user-management.delete-user-success",
    defaultMessage: "User deleted successfully."
  },
  deletingUser: {
    id: "studio.products.user-management.deleting-user",
    defaultMessage: "Deleting User"
  },
  deleteUserButton: {
    id: "studio.products.user-management.delete-user-button",
    defaultMessage: "Delete User"
  },
  deleteUserTitle: {
    id: "studio.products.user-management.delete-user-title",
    defaultMessage: "Delete User"
  },
  deleteUserConfirmation: {
    id: "studio.products.user-management.delete-user-confirmation",
    defaultMessage: "Are you sure you want to delete the User?"
  },
  manageUsers: {
    id: "studio.users.users-page.title",
    defaultMessage: "Manage Users"
  }
});

interface State {
  users: User[];
  selectedIndices: number[];
  modalDialog: JSX.Element[];
  isDeleteDialogOpen: boolean;
  isDeleting: boolean;
  error: Error;
  isSuccess: boolean;
  isExecuting: boolean;
  deleteUser: User;
}

class UsersPageComponent extends React.Component<WithApiProperties & InjectedIntlProps, State> {
  private UsersListTable: React.ComponentClass<ControlRoomTableProperties<User>>;
  _logger: Logger = Logger.getLogger("settings.users-page");

  constructor(props) {
    super(props);
    this.state = {
      users: [],
      selectedIndices: [],
      modalDialog: [],
      isDeleteDialogOpen: false,
      isDeleting: false,
      error: null,
      isSuccess: false,
      isExecuting: false,
      deleteUser: null,
    }
    this.UsersListTable = ControlRoomTable(
        new UserContentColumnFactory(
            {canEdit: true, removeItem: this.removeUser, changeItem: this.toggleUser},
        ),
        props.calculateDimensions,
    );
  }

  //case-insensitive compare
  ciEquals = (a, b) => {
    return typeof a === 'string' && typeof b === 'string'
           ? a.localeCompare(b, undefined, {sensitivity: 'accent'}) === 0
           : a === b;
  }

  private removeUser = (user: User) => {
    const {intl} = this.props;
    const userName = user.username;
    if (this.ciEquals(userName, "ADMIN") || this.ciEquals(userName, "STUDIOMANAGER")) {
      this._logger.info("Not allowed to remove " + userName + " user.");
      return this.openModal(<ModalMessageDialog
          key="remove-user-error"
          onClose={this.closeModal}
          mode={ModalMessageDialogMode.ERROR}
          body={intl.formatMessage({
            id: "studio.products.user-management.admin-user-remove-not-allowed",
            defaultMessage: "Not allowed to remove {name} user.",}, {name:userName})}
      />);
    }
    this.openDeleteDialog(user);
  };

  private toggleUser = (user: User) => {
    const {intl} = this.props;
    const userMod: User = {username: user.username, enabled: !user.enabled, roles: user.roles,};
    this.props.api.updateUser(userMod).then(() => {
      this.reloadUsers();
    }, (e) => {
      this._logger.error("Error occurred while reloading users.", e);
      this.openModal(<ModalMessageDialog
          key="update-user-error"
          onClose={this.closeModal}
          mode={ModalMessageDialogMode.ERROR}
          body={intl.formatMessage({
            id: "studio.products.user-management.update-user-error",
            defaultMessage: "Failed to update user status."
          })}
      />);
    })
  };

  componentDidMount() {
    this.reloadUsers();
  }

  private closeModal = () => {
    this.setState({...this.state, modalDialog: []})
  }

  private openModal = (modal: JSX.Element) => {
    this.setState({...this.state, modalDialog: [modal]})
  }

  private reloadUsers() {
    this.props.api.getAllUsers().then((users: User[]) => {
      this.setState({...this.state, users})
    })
  }

  private createUser = (user: User) => {
    const {intl} = this.props;
    this.props.api.createUser(user).then(() => {
          this.reloadUsers();
        },
        (e) => {
          this._logger.error("Error occurred while creating user.", e);
          this.openModal(<ModalMessageDialog
              key="create-user-error"
              onClose={this.closeModal}
              mode={ModalMessageDialogMode.ERROR}
              body={intl.formatMessage({
                id: "studio.products.user-management.create-user-error",
                defaultMessage: "Failed to create user."
              })}
          />);
        })
  }

  openDeleteDialog = (deleteUser: User) => {
    this.setState({
      ...this.state,
      isExecuting: false,
      error: null,
      isSuccess: false,
      isDeleteDialogOpen: true,
      deleteUser
    });
  }

  closeDeleteDialog = () => {
    this.setState({...this.state, isDeleteDialogOpen: false, deleteUser: null});
  }

  performDeletion = () => {
    this.setState({...this.state, isExecuting: true, error: null, isSuccess: false});
    return this.props.api.deleteUser(this.state.deleteUser).then(() => {
      this.reloadUsers();
      this.setState({...this.state, isExecuting: false, error: null, isSuccess: true});
    }).catch((e) => {
      this._logger.error("Error occurred while removing user.", e);
      const error = Error(this.props.intl.formatMessage({
        id: "studio.products.user-management.remove-user-error",
        defaultMessage: "ERROR: Failed to delete user."
      }));
      this.setState({...this.state, isExecuting: false, error, isSuccess: false});
    });
  }

  render() {
    const {intl} = this.props;
    return (
        <div>
          <ConfirmationDialog
              show={this.state.isDeleteDialogOpen}
              intl={this.props.intl}
              renderConfirmation={() => {
                return (
                    <div>
                      {intl.formatMessage(MESSAGES.deleteUserConfirmation)} <br/>
                      <strong>{this.state.deleteUser ? this.state.deleteUser.username : ""}</strong>
                    </div>)
              }}
              confirmButtonIcon={"delete"}
              confirmButtonText={intl.formatMessage(MESSAGES.deleteUserButton)}
              onConfirm={this.performDeletion}
              executing={this.state.isDeleting}
              executingMessage={intl.formatMessage(MESSAGES.deletingUser)}
              error={this.state.error}
              simpleError={true}
              success={this.state.isSuccess}
              warnings={null}
              successMessage={intl.formatMessage(MESSAGES.deleteUserSuccess)}
              title={intl.formatMessage(MESSAGES.deleteUserTitle)}
              onHide={this.closeDeleteDialog}
          />
          <h1>
            {intl.formatMessage(MESSAGES.manageUsers)}
          </h1>
          <hr/>
          <NewUserButton onSuccess={this.createUser} api={this.props.api}/>
          <this.UsersListTable data={this.state.users}
                               intl={intl}
                               isPaged={false}
                               canMultiSelect={true}
                               isAllDataLoaded={true}
                               selectedRowIndices={this.state.selectedIndices}
                               onRowSelect={() => {/*Not used*/
                               }}
                               rowHeight={USERS_DATA_LIST_ROW_HEIGHT}
          />
          {this.state.modalDialog}
        </div>
    );
  }
}

export const UsersPage = injectIntl(WithApi(UsersPageComponent));